import { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Form,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Tag,
  Spin,
  Alert,
  App,
  Table,
  Badge,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  ClearOutlined,
  EyeOutlined,
  CopyOutlined,
  SearchOutlined,
  ApiOutlined,
  FilterOutlined,
  DatabaseOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosConfig";
import DashboardLayout from "./DashboardLayout";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const OPERATORS = [
  { value: "equals", label: "Equals (=)", icon: "=" },
  { value: "notEquals", label: "Not Equals (!=)", icon: "≠" },
  { value: "contains", label: "Contains", icon: "⊃" },
  { value: "notContains", label: "Not Contains", icon: "⊅" },
  { value: "startsWith", label: "Starts With", icon: "^" },
  { value: "endsWith", label: "Ends With", icon: "$" },
  { value: "greaterThan", label: "Greater Than (>)", icon: ">" },
  { value: "lessThan", label: "Less Than (<)", icon: "<" },
  {
    value: "greaterThanOrEqual",
    label: "Greater Than or Equal (>=)",
    icon: "≥",
  },
  { value: "lessThanOrEqual", label: "Less Than or Equal (<=)", icon: "≤" },
  { value: "in", label: "In (array)", icon: "∈" },
  { value: "notIn", label: "Not In (array)", icon: "∉" },
];

const DEFAULT_FIELDS = [
  { value: "id", label: "ID", icon: "🔑" },
  { value: "name", label: "Name", icon: "👤" },
  { value: "email", label: "Email", icon: "📧" },
  { value: "username", label: "Username", icon: "💬" },
  { value: "createdAt", label: "Created At", icon: "📅" },
  { value: "updatedAt", label: "Updated At", icon: "🔄" },
  { value: "status", label: "Status", icon: "✅" },
];

export default function RESTQueryBuilder() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [requestPreview, setRequestPreview] = useState("");
  const [requestUrl, setRequestUrl] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 10,
    hasMore: true,
  });
  const [apiEndpoint, setApiEndpoint] = useState("/api/users");
  const observerTarget = useRef(null);

  // Watch form values for request preview
  const formValues = Form.useWatch([], form);

  // Generate REST API request preview
  useEffect(() => {
    if (formValues) {
      generateRequestPreview();
    }
  }, [formValues]);

  const generateRequestPreview = () => {
    try {
      const filters = formValues?.filters || [];
      const endpoint = apiEndpoint || "/api/users";
      const paginationObj = formValues?.pagination || { offset: 0, limit: 10 };

      const validFilters = filters.filter(
        (f) =>
          f?.field && f?.operator && f?.value !== undefined && f?.value !== ""
      );

      // Build query parameters
      const queryParams = new URLSearchParams();

      validFilters.forEach((filter) => {
        queryParams.append(
          `filters[${filter.field}][operator]`,
          filter.operator
        );
        queryParams.append(`filters[${filter.field}][value]`, filter.value);
      });

      queryParams.append("offset", (paginationObj.offset || 0).toString());
      queryParams.append("limit", (paginationObj.limit || 10).toString());

      const queryString = queryParams.toString();
      const fullUrl = queryString ? `${endpoint}?${queryString}` : endpoint;

      setRequestUrl(fullUrl);

      // Get actual API key from localStorage
      const apiKey = localStorage.getItem("apiKey") || "[YOUR_API_KEY]";

      // Generate preview text
      const preview = `GET ${fullUrl}\n\nHeaders:\nContent-Type: application/json\nx-api-key: ${apiKey}`;

      setRequestPreview(preview);
    } catch (error) {
      console.error("Error generating request preview:", error);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(requestPreview);
    message.success("Request preview copied to clipboard!", 2);
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const validFilters = (values.filters || []).filter(
        (f) =>
          f?.field && f?.operator && f?.value !== undefined && f?.value !== ""
      );

      const endpoint = apiEndpoint || "/api/users";
      const paginationObj = values.pagination || { offset: 0, limit: 10 };

      // Build query parameters for GET
      const queryParams = new URLSearchParams();

      validFilters.forEach((filter) => {
        queryParams.append(
          `filters[${filter.field}][operator]`,
          filter.operator
        );
        queryParams.append(`filters[${filter.field}][value]`, filter.value);
      });

      queryParams.append("offset", paginationObj.offset.toString());
      queryParams.append("limit", paginationObj.limit.toString());

      const queryString = queryParams.toString();
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;

      const response = await axiosInstance.get(url);

      if (response.data) {
        // Handle different response structures
        const data =
          response.data.data ||
          response.data.items ||
          response.data.results ||
          response.data;
        const newResults = Array.isArray(data)
          ? data
          : data?.items || data?.results || [];

        setResults(newResults);
        setPagination({
          offset: 0,
          limit: paginationObj.limit || pagination.limit,
          hasMore:
            newResults.length === (paginationObj.limit || pagination.limit),
        });
        message.success("Query executed successfully!", 3);
      }
    } catch (error) {
      console.error("Error executing query:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to execute API request";
      message.error(errorMessage, 5);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loading || !pagination.hasMore) return;

    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const validFilters = (values.filters || []).filter(
        (f) =>
          f?.field && f?.operator && f?.value !== undefined && f?.value !== ""
      );

      const endpoint = apiEndpoint || "/api/users";
      const newOffset = pagination.offset + pagination.limit;

      const queryParams = new URLSearchParams();

      validFilters.forEach((filter) => {
        queryParams.append(
          `filters[${filter.field}][operator]`,
          filter.operator
        );
        queryParams.append(`filters[${filter.field}][value]`, filter.value);
      });

      queryParams.append("offset", newOffset.toString());
      queryParams.append("limit", pagination.limit.toString());

      const queryString = queryParams.toString();
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;

      const response = await axiosInstance.get(url);

      if (response.data) {
        const data =
          response.data.data ||
          response.data.items ||
          response.data.results ||
          response.data;
        const newResults = Array.isArray(data)
          ? data
          : data?.items || data?.results || [];

        if (newResults.length > 0) {
          setResults((prev) => [...prev, ...newResults]);
          setPagination((prev) => ({
            ...prev,
            offset: prev.offset + prev.limit,
            hasMore: newResults.length === prev.limit,
          }));
        } else {
          setPagination((prev) => ({ ...prev, hasMore: false }));
        }
      }
    } catch (error) {
      console.error("Error loading more:", error);
      setPagination((prev) => ({ ...prev, hasMore: false }));
    } finally {
      setLoading(false);
    }
  }, [loading, pagination, apiEndpoint, form]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pagination.hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMore, pagination.hasMore, loading]);

  const clearFilters = () => {
    form.setFieldsValue({
      filters: [{ field: "name", operator: "contains", value: "" }],
      pagination: { offset: 0, limit: 10 },
    });
    setResults([]);
    setPagination({ offset: 0, limit: 10, hasMore: true });
  };

  const columns =
    results.length > 0
      ? Object.keys(results[0]).map((key) => ({
          title: key.charAt(0).toUpperCase() + key.slice(1),
          dataIndex: key,
          key: key,
          render: (text) => {
            if (typeof text === "object") {
              return JSON.stringify(text);
            }
            return text?.toString() || "-";
          },
        }))
      : [];

  const activeFiltersCount =
    formValues?.filters?.filter(
      (f) =>
        f?.field && f?.operator && f?.value !== undefined && f?.value !== ""
    ).length || 0;

  return (
    <DashboardLayout title="REST API Query Builder">
      {/* Header Info Card */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 16,
          background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
          border: "none",
          boxShadow: "0 8px 24px rgba(0, 102, 255, 0.2)",
        }}
        styles={{ body: { padding: "24px 32px" } }}
      >
        <Row align="middle" gutter={24}>
          <Col flex="auto">
            <Space size="large">
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(10px)",
                }}
              >
                <SearchOutlined style={{ fontSize: 28, color: "#ffffff" }} />
              </div>
              <div>
                <Title
                  level={3}
                  style={{
                    margin: 0,
                    color: "#ffffff",
                    fontWeight: 700,
                    marginBottom: 4,
                  }}
                >
                  Query Builder
                </Title>
                <Text
                  style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: 14 }}
                >
                  Build and execute GET queries safely with filters and
                  pagination
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Badge count={activeFiltersCount} showZero={false}>
                <Tag
                  icon={<FilterOutlined />}
                  color="rgba(255, 255, 255, 0.3)"
                  style={{
                    color: "#ffffff",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    padding: "8px 16px",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  Active Filters
                </Tag>
              </Badge>
              <Tag
                icon={<SafetyOutlined />}
                color="rgba(255, 255, 255, 0.3)"
                style={{
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  padding: "8px 16px",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                GET Only - Safe Mode
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]}>
        {/* Query Builder Form */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ApiOutlined style={{ fontSize: 20, color: "#ffffff" }} />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  Build Query
                </Title>
              </Space>
            }
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0, 102, 255, 0.1)",
              border: "1px solid #e1e8f0",
            }}
            styles={{ body: { padding: 24 } }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              initialValues={{
                filters: [{ field: "name", operator: "contains", value: "" }],
                pagination: { offset: 0, limit: 10 },
              }}
            >
              <Space
                orientation="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <Form.Item
                  label={
                    <Space>
                      <DatabaseOutlined style={{ color: "#0066ff" }} />
                      <Text strong>API Endpoint</Text>
                    </Space>
                  }
                >
                  <Input
                    value={apiEndpoint}
                    onChange={(e) => setApiEndpoint(e.target.value)}
                    placeholder="/api/users"
                    size="large"
                    prefix={<ApiOutlined style={{ color: "#0066ff" }} />}
                    style={{
                      borderRadius: 10,
                      border: "2px solid #e1e8f0",
                    }}
                  />
                </Form.Item>

                <Divider orientation="left">
                  <Space>
                    <FilterOutlined style={{ color: "#0066ff" }} />
                    <Text strong style={{ fontSize: 16 }}>
                      Filters
                    </Text>
                  </Space>
                </Divider>

                <Form.List name="filters">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card
                          key={key}
                          size="small"
                          style={{
                            background:
                              "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
                            border: "2px solid #e1e8f0",
                            borderRadius: 12,
                            boxShadow: "0 2px 8px rgba(0, 102, 255, 0.05)",
                          }}
                          styles={{ body: { padding: 16 } }}
                        >
                          <Row gutter={[12, 12]}>
                            <Col xs={24} sm={7}>
                              <Form.Item
                                {...restField}
                                name={[name, "field"]}
                                label={
                                  <Text strong style={{ fontSize: 12 }}>
                                    Field
                                  </Text>
                                }
                                rules={[
                                  { required: true, message: "Select field" },
                                ]}
                              >
                                <Select
                                  placeholder="Select field"
                                  style={{ width: "100%" }}
                                  size="large"
                                  showSearch
                                  optionFilterProp="children"
                                >
                                  {DEFAULT_FIELDS.map((f) => (
                                    <Option key={f.value} value={f.value}>
                                      <Space>
                                        <span>{f.icon}</span>
                                        <span>{f.label}</span>
                                      </Space>
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={7}>
                              <Form.Item
                                {...restField}
                                name={[name, "operator"]}
                                label={
                                  <Text strong style={{ fontSize: 12 }}>
                                    Operator
                                  </Text>
                                }
                                rules={[
                                  {
                                    required: true,
                                    message: "Select operator",
                                  },
                                ]}
                              >
                                <Select
                                  placeholder="Select operator"
                                  style={{ width: "100%" }}
                                  size="large"
                                  showSearch
                                  optionFilterProp="children"
                                >
                                  {OPERATORS.map((op) => (
                                    <Option key={op.value} value={op.value}>
                                      <Space>
                                        <Text strong style={{ fontSize: 16 }}>
                                          {op.icon}
                                        </Text>
                                        <span>{op.label}</span>
                                      </Space>
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={7}>
                              <Form.Item
                                {...restField}
                                name={[name, "value"]}
                                label={
                                  <Text strong style={{ fontSize: 12 }}>
                                    Value
                                  </Text>
                                }
                                rules={[
                                  { required: true, message: "Enter value" },
                                ]}
                              >
                                <Input
                                  placeholder="Enter value"
                                  size="large"
                                  style={{
                                    borderRadius: 10,
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={3}>
                              <Form.Item label=" ">
                                <Tooltip title="Remove filter">
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(name)}
                                    disabled={fields.length === 1}
                                    size="large"
                                    style={{
                                      borderRadius: 8,
                                    }}
                                  />
                                </Tooltip>
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() =>
                          add({ field: "", operator: "equals", value: "" })
                        }
                        block
                        size="large"
                        style={{
                          borderRadius: 10,
                          border: "2px dashed #0066ff",
                          color: "#0066ff",
                          fontWeight: 600,
                          height: 48,
                        }}
                      >
                        Add Filter
                      </Button>
                    </>
                  )}
                </Form.List>

                <Divider orientation="left">
                  <Space>
                    <DatabaseOutlined style={{ color: "#0066ff" }} />
                    <Text strong style={{ fontSize: 16 }}>
                      Pagination
                    </Text>
                  </Space>
                </Divider>

                <Row gutter={16}>
                  <Col xs={12}>
                    <Form.Item
                      name={["pagination", "offset"]}
                      label={<Text strong>Offset</Text>}
                      rules={[{ required: true, message: "Enter offset" }]}
                    >
                      <Input
                        type="number"
                        min={0}
                        size="large"
                        style={{
                          borderRadius: 10,
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      name={["pagination", "limit"]}
                      label={<Text strong>Limit</Text>}
                      rules={[
                        { required: true, message: "Enter limit" },
                        {
                          type: "number",
                          min: 1,
                          max: 100,
                          message: "Limit must be between 1 and 100",
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        size="large"
                        style={{
                          borderRadius: 10,
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Space
                  style={{
                    width: "100%",
                    justifyContent: "flex-end",
                    marginTop: 8,
                  }}
                >
                  <Button
                    icon={<ClearOutlined />}
                    onClick={clearFilters}
                    size="large"
                    style={{
                      borderRadius: 10,
                      height: 44,
                      fontWeight: 600,
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<PlayCircleOutlined />}
                    loading={loading}
                    size="large"
                    style={{
                      background:
                        "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
                      border: "none",
                      borderRadius: 10,
                      height: 44,
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(0, 102, 255, 0.3)",
                    }}
                  >
                    Execute Query
                  </Button>
                </Space>
              </Space>
            </Form>
          </Card>
        </Col>

        {/* Request Preview */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #00d9ff 0%, #0066ff 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EyeOutlined style={{ fontSize: 20, color: "#ffffff" }} />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  Request Preview
                </Title>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={copyToClipboard}
                  size="small"
                  style={{
                    marginLeft: "auto",
                    color: "#0066ff",
                  }}
                >
                  Copy
                </Button>
              </Space>
            }
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0, 102, 255, 0.1)",
              border: "1px solid #e1e8f0",
            }}
            styles={{ body: { padding: 24 } }}
          >
            <TextArea
              value={requestPreview}
              readOnly
              rows={12}
              style={{
                fontFamily: "monospace",
                fontSize: 13,
                background: "#f8faff",
                border: "2px solid #e1e8f0",
                borderRadius: 10,
                padding: 16,
              }}
            />
            <Divider style={{ margin: "16px 0" }} />
            <Space
              orientation="vertical"
              size="small"
              style={{ width: "100%" }}
            >
              <Text strong style={{ fontSize: 14 }}>
                Request URL:
              </Text>
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
                  padding: 16,
                  borderRadius: 10,
                  fontSize: 13,
                  wordBreak: "break-all",
                  border: "2px solid #e1e8f0",
                  fontFamily: "monospace",
                  color: "#0066ff",
                  fontWeight: 500,
                }}
              >
                {requestUrl || "No URL generated"}
              </div>
            </Space>
          </Card>
        </Col>

        {/* Results */}
        <Col xs={24}>
          <Card
            title={
              <Space>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DatabaseOutlined
                    style={{ fontSize: 20, color: "#ffffff" }}
                  />
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  Results
                </Title>
                {results.length > 0 && (
                  <Tag
                    color="blue"
                    style={{
                      fontSize: 14,
                      padding: "4px 12px",
                      borderRadius: 8,
                      fontWeight: 600,
                    }}
                  >
                    {results.length} records
                  </Tag>
                )}
              </Space>
            }
            style={{
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(0, 102, 255, 0.1)",
              border: "1px solid #e1e8f0",
            }}
            styles={{ body: { padding: 24 } }}
          >
            {results.length === 0 && !loading ? (
              <Alert
                message="No results"
                description="Execute a query to see results here"
                type="info"
                showIcon
                style={{
                  borderRadius: 12,
                  border: "2px solid #e1e8f0",
                }}
              />
            ) : (
              <>
                <Table
                  columns={columns}
                  dataSource={results.map((item, index) => ({
                    ...item,
                    key: item.id || index,
                  }))}
                  pagination={false}
                  scroll={{ x: "max-content" }}
                  style={{
                    borderRadius: 12,
                  }}
                />
                {pagination.hasMore && (
                  <div
                    ref={observerTarget}
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background:
                        "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
                      borderRadius: 12,
                      marginTop: 16,
                    }}
                  >
                    <Spin size="large" />
                    <div style={{ marginTop: 12 }}>
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        Loading more results...
                      </Text>
                    </div>
                  </div>
                )}
                {!pagination.hasMore && results.length > 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      background:
                        "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
                      borderRadius: 12,
                      marginTop: 16,
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{ fontSize: 14, fontWeight: 500 }}
                    >
                      ✓ All results loaded
                    </Text>
                  </div>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </DashboardLayout>
  );
}
